import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(dt.getDate()).padStart(2, '0')} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
};

const getValidityDate = (e) => {
  if (e?.validity_date) return fmtDate(e.validity_date);
  if (e?.validity_notes) {
    const dateMatch = e.validity_notes.match(/\b\d{4}-\d{2}-\d{2}\b/) || e.validity_notes.match(/\b\d{2}[/-]\d{2}[/-]\d{4}\b/);
    if (dateMatch) return fmtDate(dateMatch[0]);
    const daysMatch = e.validity_notes.match(/(\d+)\s*days?/i);
    if (daysMatch && daysMatch[1] && e.estimate_date) {
      const dt = new Date(e.estimate_date);
      if (!isNaN(dt.getTime())) {
        dt.setDate(dt.getDate() + parseInt(daysMatch[1], 10));
        return fmtDate(dt);
      }
    }
    if (e.validity_notes.length <= 25) return e.validity_notes;
  }
  if (e?.estimate_date) {
    const dt = new Date(e.estimate_date);
    if (!isNaN(dt.getTime())) {
      dt.setDate(dt.getDate() + 7);
      return fmtDate(dt);
    }
  }
  return '-';
};

const fmtAmt = (v, curr = '₹') => {
  if (v === null || v === undefined || v === '') return '-';
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return `${curr} ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtNum = (v, decimals = 2) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return n % 1 === 0 ? String(n) : n.toFixed(decimals);
};

const formatTat = (tat) => {
  if (!tat || !String(tat).trim()) return '-';
  const s = String(tat).trim();
  if (/hours?|hrs?|days?/i.test(s)) return s;
  const num = parseFloat(s);
  if (!isNaN(num)) {
    return num >= 12 ? `${num} Hours` : `${num} Day${num > 1 ? 's' : ''}`;
  }
  return s;
};

// Convert Google Drive URL to direct thumbnail URL
const getCleanLogoUrl = (url) => {
  if (!url) return null;
  const match = url.match(/id=([^&]+)/) || url.match(/\/d\/([^/]+)/);
  if (match && match[1]) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w500`;
  }
  return url;
};

// Convert number to Indian currency words
const numberToWordsINR = (amount, currency = 'INR') => {
  if (!amount || isNaN(amount)) return '';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (num) => {
    if (num === 0) return '';
    let str = '';
    if (num >= 10000000) {
      str += convert(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }
    if (num >= 100000) {
      str += convert(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    if (num >= 1000) {
      str += convert(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    if (num >= 100) {
      str += convert(Math.floor(num / 100)) + ' Hundred ';
      num %= 100;
    }
    if (num > 0) {
      if (str !== '') str += 'and ';
      if (num < 20) str += a[num] + ' ';
      else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
    }
    return str.trim();
  };

  const whole = Math.floor(Math.abs(amount));
  const fraction = Math.round((Math.abs(amount) - whole) * 100);
  const prefix = currency === 'INR' ? 'Rupees ' : `${currency} `;
  let words = prefix + convert(whole);
  if (fraction > 0) {
    words += ` and ${convert(fraction)} Paise`;
  }
  return words + ' Only';
};

// ─── Modern Print CSS Styles ──────────────────────────────────────────────────
const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 8mm 10mm 10mm 10mm;
  }
  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      font-size: 12px !important;
      font-family: Arial, sans-serif !important;
      height: auto !important;
      min-height: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden !important;
    }
    #estimate-print-content, #estimate-print-content * {
      visibility: visible !important;
    }
    #estimate-print-content {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
      background: #fff !important;
    }
    .no-print,
    .no-print *,
    [data-no-print="true"],
    [data-no-print="true"] *,
    .print-actions,
    .print-actions *,
    .floating-toolbar,
    .floating-toolbar *,
    .print\:hidden {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      width: 0 !important;
      max-height: 0 !important;
      max-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      position: absolute !important;
      left: -99999px !important;
      top: -99999px !important;
      overflow: hidden !important;
      pointer-events: none !important;
    }
    .page-break { page-break-before: always; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    .print-container { width: 100% !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
  }
  @media screen {
    body { background: #f3f4f6; }
    .print-container {
      max-width: 880px;
      margin: 20px auto 50px auto;
      background: #fff;
      padding: 28px 32px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      border-radius: 8px;
    }
  }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, sans-serif !important;
    font-size: 12px;
    line-height: 1.45;
    color: #1e293b;
    margin: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 5px 7px; font-size: 12px; font-family: Arial, sans-serif; }
  .table-bordered th, .table-bordered td {
    border: 1px solid #94a3b8 !important;
    line-height: 1.4;
  }
  .table-bordered th {
    background: #f1f5f9;
    color: #0f172a;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.2px;
    padding: 6px 8px;
    font-family: Arial, sans-serif;
  }
  .table-bordered td {
    font-size: 12px;
    font-family: Arial, sans-serif;
  }
`;

export default function EstimatePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const estRes = await fetch(`${API}/api/estimates/${id}`, { headers: authHeaders() });
      if (!estRes.ok) throw new Error('Estimate not found');
      const est = await estRes.json();
      setEstimate(est);

      // Fetch company for branding (try embedded company first, then specific company endpoint, then company list)
      let cmp = est.company || null;
      if (!cmp && est.company_id) {
        try {
          const cmpRes = await fetch(`${API}/api/companies/${est.company_id}`, { headers: authHeaders() });
          if (cmpRes.ok) cmp = await cmpRes.json();
        } catch {}
      }
      if (!cmp) {
        try {
          const cmpRes = await fetch(`${API}/api/companies`, { headers: authHeaders() });
          if (cmpRes.ok) {
            const cmps = await cmpRes.json();
            cmp = cmps.find(c => c.id === est.company_id) || cmps[0] || null;
          }
        } catch {}
      }
      setCompany(cmp);
    } catch (err) {
      alert(err.message || 'Failed to load estimate for printing');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Set document title so browser "Save as PDF" defaults filename to Estimate Number
  useEffect(() => {
    if (estimate?.estimate_no) {
      document.title = estimate.estimate_no;
    }
    return () => {
      document.title = 'LOGI';
    };
  }, [estimate]);

  const [isPrinting, setIsPrinting] = useState(false);
  const toolbarRef = useRef(null);

  // Sync toolbar suppression on native print dialog triggers (Ctrl+P, browser menu, etc.)
  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
      if (toolbarRef.current) {
        toolbarRef.current.style.setProperty('display', 'none', 'important');
      }
    };
    const handleAfterPrint = () => {
      setIsPrinting(false);
      if (toolbarRef.current) {
        toolbarRef.current.style.removeProperty('display');
      }
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  const handlePrint = () => {
    if (estimate?.estimate_no) {
      document.title = estimate.estimate_no;
    }
    setIsPrinting(true);
    if (toolbarRef.current) {
      toolbarRef.current.style.setProperty('display', 'none', 'important');
    }
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setIsPrinting(false);
        if (toolbarRef.current) {
          toolbarRef.current.style.removeProperty('display');
        }
      }, 500);
    }, 60);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', fontFamily: 'Arial' }}>
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading quotation print preview...</p>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div style={{ textAlign: 'center', padding: 80, fontFamily: 'Arial' }}>
        <h3>Quotation / Estimate not found</h3>
        <button onClick={() => navigate('/estimates')} style={{ padding: '8px 16px', marginTop: 12, cursor: 'pointer', fontSize: '13px' }}>
          Back to Estimates
        </button>
      </div>
    );
  }

  const e = estimate;
  const CURRENCY_SYMBOLS = {
    INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', AED: 'AED', SGD: 'S$', CNY: '¥', JPY: '¥', CAD: 'C$', CHF: 'CHF', HKD: 'HK$', SAR: 'SAR', MYR: 'RM', THB: '฿', KWD: 'KWD', OMR: 'OMR', QAR: 'QAR', BHD: 'BHD', NZD: 'NZ$',
  };
  const currSymbol = CURRENCY_SYMBOLS[e.currency] || (e.currency ? `${e.currency} ` : '₹');

  // Company details with fallbacks
  const cmpName = company?.name || 'STAVAN INTERNATIONAL LLP';
  const cmpAddr = [company?.address, company?.city, company?.state, company?.postal_code || company?.pincode, company?.country]
    .filter(Boolean).join(', ') || 'Reg. Off: 737-738, Sun Gravitas, Radio Mirchi Road, Vejalpur, Ahmedabad - 380051, Gujarat, India';
  const cmpPhone = company?.phone || '+91-79-40088280';
  const cmpEmail = company?.email || 'admin@stavan.co.in';
  const cmpWeb = company?.website || 'www.stavan.co.in';
  const cmpGst = company?.gst_number || '24ADXFS3875R1ZR';
  const cmpPan = company?.pan_number;

  const grandTotal = (e.charge_groups || []).reduce((s, g) => s + (parseFloat(g.group_total) || 0), 0);

  // Parse remarks
  let remarksList = [];
  if (e.remarks_json) {
    try {
      const parsed = JSON.parse(e.remarks_json);
      if (Array.isArray(parsed)) remarksList = parsed.filter(Boolean);
    } catch {}
  }

  // Parse dimensions from dimensions_json or legacy packages
  let dimensionRows = [];
  if (e.dimensions_json) {
    try {
      const parsed = JSON.parse(e.dimensions_json);
      if (Array.isArray(parsed)) dimensionRows = parsed;
    } catch {}
  }
  if (dimensionRows.length === 0 && (e.packages || []).length > 0) {
    dimensionRows = e.packages.map(p => {
      const parts = (p.dimension || '').split('x');
      return {
        package_type: p.package_type || 'Boxes',
        packages: String(p.packages || p.no_of_packages || '1'),
        gross_weight: p.gross_weight !== undefined && p.gross_weight !== null ? String(p.gross_weight) : '',
        length: parts[0] || '',
        width: parts[1] || '',
        height: parts[2] || '',
        no_of_packages: String(p.no_of_packages || p.packages || '1'),
        cbm: '',
        vol_weight: p.volume_weight ? String(p.volume_weight) : '',
      };
    });
  }

  const dimUnit = e.dimension_unit || 'CM';
  const isAirMode = e.mode_of_transport === 'AIR';
  const isSeaMode = e.mode_of_transport === 'SEA';
  const isFCL = isSeaMode && e.sea_load_type === 'FCL';
  const isRoadRail = e.mode_of_transport === 'ROAD' || e.mode_of_transport === 'RAIL';

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />

      {/* ── Screen Floating Action Bar (Suppressed in Print) ───────────── */}
      {!isPrinting && (
        <div
          ref={toolbarRef}
          className="no-print print:hidden print-actions floating-toolbar"
          data-no-print="true"
          style={{
            position: 'fixed',
            top: 14,
            right: 20,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(255,255,255,0.95)',
            padding: '6px 14px',
            borderRadius: '30px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(8px)',
            border: '1px solid #e2e8f0'
          }}
        >
          <button
            type="button"
            onClick={handlePrint}
            style={{
              padding: '7px 16px',
              background: '#1d4ed8',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print / Save PDF
          </button>
          <button
            type="button"
            onClick={() => navigate('/estimates')}
            style={{
              padding: '7px 14px',
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* ── Main Printable Quotation Document ────────────────────────── */}
      <div id="estimate-print-content" className="print-container" style={{ fontFamily: "Arial, sans-serif", fontSize: "12px", lineHeight: "1.4", color: "#000" }}>

        {/* ── 1. Elegant Header & Company Branding ──────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '10px' }}>
          {/* Company Brand Logo / Emblem */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {company?.logo_url ? (
              <img
                src={getCleanLogoUrl(company.logo_url)}
                alt={cmpName}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                style={{ maxHeight: '66px', maxWidth: '190px', objectFit: 'contain' }}
                onError={(e) => {
                  console.error('Estimate logo load error, trying fallback:', company.logo_url);
                  const match = company.logo_url.match(/id=([^&]+)/) || company.logo_url.match(/\/d\/([^/]+)/);
                  if (match && match[1] && !e.target.dataset.retried) {
                    e.target.dataset.retried = 'true';
                    e.target.src = `https://lh3.googleusercontent.com/d/${match[1]}=w400`;
                  }
                }}
              />
            ) : (
              <div style={{
                background: '#0f172a',
                color: '#fff',
                width: '50px',
                height: '50px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '900',
                fontSize: '24px',
                letterSpacing: '1px'
              }}>
                {cmpName.charAt(0)}
              </div>
            )}
            <div>
              <div style={{
                fontSize: '19px',
                fontWeight: '800',
                color: '#0f172a',
                letterSpacing: '0.6px',
                lineHeight: 1.15,
                textTransform: 'uppercase'
              }}>
                {cmpName}
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1.5px',
                color: '#d97706',
                marginTop: '2px',
                textTransform: 'uppercase'
              }}>
                Global Freight Forwarding & Logistics Solutions
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>
                Customs Broker • Reefer Transport • Air & Ocean Cargo
              </div>
            </div>
          </div>

          {/* Right Header: Quotation Badge & Contacts */}
          <div style={{ textAlign: 'right', fontSize: '12px', lineHeight: '1.35' }}>
            <div style={{
              display: 'inline-block',
              background: '#0f172a',
              color: '#fff',
              fontSize: '12.5px',
              fontWeight: '700',
              padding: '2.5px 10px',
              borderRadius: '3px',
              letterSpacing: '0.8px',
              marginBottom: '4px'
            }}>
              FREIGHT QUOTATION
            </div>
            <div style={{ color: '#475569', fontSize: '12px' }}>
              <div><strong style={{ color: '#0f172a' }}>Tel: </strong>{cmpPhone}</div>
              <div><strong style={{ color: '#0f172a' }}>Email: </strong>{cmpEmail}</div>
              <div style={{ color: '#1d4ed8', fontWeight: '500' }}>{cmpWeb}</div>
            </div>
          </div>
        </div>

        {/* Navy & Gold Divider Line */}
        <div style={{
          height: '2.5px',
          background: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 60%, #f59e0b 100%)',
          marginBottom: '12px',
          borderRadius: '2px'
        }} />

        {/* ── 2. Two-Column Metadata Box: Customer + Quotation Details (50% - 50%) ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          marginBottom: '12px'
        }}>
          {/* Customer / Consignee Block */}
          <div style={{
            border: '1px solid #94a3b8',
            borderRadius: '5px',
            padding: '8px 12px',
            background: '#fafafa'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '700',
              textTransform: 'uppercase',
              color: '#475569',
              letterSpacing: '0.6px',
              marginBottom: '3px'
            }}>
              Quotation Prepared For
            </div>
            <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', lineHeight: 1.25 }}>
              {e.party_name || 'Valued Customer'}
            </div>
            {e.attention && (
              <div style={{ fontSize: '12px', color: '#334155', marginTop: '3px' }}>
                <span style={{ fontWeight: 'bold', color: '#0f172a' }}>Attn: </span>
                {e.attention}
              </div>
            )}
            {e.place && (
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                <span style={{ fontWeight: 'bold', color: '#0f172a' }}>Place/City: </span>
                {e.place}
              </div>
            )}
          </div>

          {/* Quotation Metadata Card */}
          <div style={{
            border: '1px solid #94a3b8',
            borderRadius: '5px',
            padding: '8px 12px',
            background: '#f8fafc'
          }}>
            <table style={{ width: '100%', fontSize: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0', width: '42%' }}>Quote Ref No:</td>
                  <td style={{ fontWeight: 'bold', color: '#1e3a8a', padding: '2px 0' }}>{e.estimate_no || '-'}</td>
                </tr>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0' }}>Quote Date:</td>
                  <td style={{ fontWeight: 'bold', color: '#0f172a', padding: '2px 0' }}>{fmtDate(e.estimate_date)}</td>
                </tr>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0' }}>Validity Date:</td>
                  <td style={{ fontWeight: 'bold', color: '#0f172a', padding: '2px 0' }}>{getValidityDate(e)}</td>
                </tr>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0' }}>Shipment Scope:</td>
                  <td style={{ fontWeight: 'bold', color: '#0f172a', padding: '2px 0' }}>
                    {e.shipment_scope || 'INTERNATIONAL'} • {e.movement_direction || 'INWARD'}
                  </td>
                </tr>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0' }}>Mode of Transport:</td>
                  <td style={{ fontWeight: 'bold', color: '#0f172a', padding: '2px 0' }}>
                    <span style={{
                      background: e.mode_of_transport === 'AIR' ? '#dbeafe' : e.mode_of_transport === 'SEA' ? '#ccfbf1' : '#fef3c7',
                      color: e.mode_of_transport === 'AIR' ? '#1e40af' : e.mode_of_transport === 'SEA' ? '#0f766e' : '#92400e',
                      padding: '1px 6px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {e.mode_of_transport} {isSeaMode ? `(${e.sea_load_type || 'LCL'})` : ''}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: '#0f172a', fontWeight: 'bold', padding: '2px 0' }}>{e.shipment_scope === 'DOMESTIC' ? 'Freight Term:' : 'Incoterm:'}</td>
                  <td style={{ fontWeight: 'bold', color: '#b45309', padding: '2px 0' }}>{e.term || (e.shipment_scope === 'DOMESTIC' ? 'PAID' : 'EXW')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 3. Subject & Intro Note ────────────────────────────────── */}
        {(e.subject || e.intro_remarks) && (
          <div style={{
            background: '#f8fafc',
            borderLeft: '3.5px solid #1e3a8a',
            padding: '8px 12px',
            borderRadius: '0 4px 4px 0',
            marginBottom: '12px',
            fontSize: '12px'
          }}>
            {e.subject && (
              <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: e.intro_remarks ? '3px' : '0' }}>
                <span style={{ color: '#64748b' }}>SUBJECT: </span>{e.subject}
              </div>
            )}
            {e.intro_remarks && (
              <div style={{ color: '#334155', fontStyle: 'italic', marginTop: '2px' }}>
                {e.intro_remarks}
              </div>
            )}
          </div>
        )}

        {/* ── 4. Shipment Movement & Specifications Table (5 Columns, Headers 13px, Data 11px) ── */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            background: '#0f172a',
            color: '#fff',
            fontWeight: '700',
            fontSize: '13px',
            letterSpacing: '0.6px',
            padding: '5px 10px',
            textTransform: 'uppercase',
            borderRadius: '4px 4px 0 0'
          }}>
            Shipment Routing &amp; Specifications
          </div>

          {(e.mode_of_transport === 'ROAD' || e.mode_of_transport === 'RAIL') ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ width: '18%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    Origin (From)
                  </th>
                  <th style={{ width: '18%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    Destination (To)
                  </th>
                  <th style={{ width: '14%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    Load Type
                  </th>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    Vehicle Type &amp; Size
                  </th>
                  <th style={{ width: '15%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    TAT (Transit Time)
                  </th>
                  <th style={{ width: '15%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', color: '#1e293b', fontSize: '13px', fontWeight: '700' }}>
                    Service
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: '#ffffff' }}>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontWeight: '600', fontSize: '12px', color: '#0f172a' }}>
                    {e.road_from || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontWeight: '600', fontSize: '12px', color: '#0f172a' }}>
                    {e.road_to || e.road_location || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontSize: '12px', fontWeight: '700', color: '#0369a1' }}>
                    {e.road_load_type ? (e.road_load_type === 'PTL' ? 'PTL (Part Load)' : 'FTL (Full Truck)') : 'FTL'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontSize: '12px' }}>
                    {[e.road_vehicle_type, e.road_vehicle_size].filter(Boolean).join(' - ') || (e.road_load_type === 'PTL' ? 'Part Truck / LCV' : '-')}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontSize: '12px', fontWeight: '600', color: '#0f172a' }}>
                    {formatTat(e.road_tat)}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', fontSize: '12px', fontWeight: '700', color: '#1e3a8a' }}>
                    {e.service || '-'}
                  </td>
                </tr>
                {(e.road_material_type || (e.road_load_type === 'PTL' && e.road_weight_slab)) && (
                  <tr>
                    <td colSpan={6} style={{ border: '1px solid #94a3b8', padding: '4px 8px', fontSize: '12px', background: '#f8fafc' }}>
                      {e.road_material_type && (
                        <span>
                          <span style={{ color: '#0f172a', fontWeight: 'bold' }}>Material Type: </span>
                          <strong>{e.road_material_type}</strong>
                        </span>
                      )}
                      {e.road_load_type === 'PTL' && e.road_weight_slab && (
                        <span style={{ marginLeft: e.road_material_type ? '24px' : '0' }}>
                          <span style={{ color: '#0f172a', fontWeight: 'bold' }}>Weight Slab: </span>
                          <strong>{e.road_weight_slab} {e.road_rate_per_kg ? `(@ ₹${e.road_rate_per_kg}/kg)` : ''}</strong>
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                    Pick Up / Origin
                  </th>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                    {isSeaMode ? 'Port of Loading (POL)' : 'Airport of Loading (AOL)'}
                  </th>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                    {isSeaMode ? 'Port of Discharge (POD)' : 'Airport of Discharge (AOD)'}
                  </th>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                    Final Delivery Destination
                  </th>
                  <th style={{ width: '20%', border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', fontSize: '13px', color: '#1e293b', fontWeight: '700' }}>
                    Service
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: '#ffffff' }}>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', verticalAlign: 'top', fontSize: '12px', fontWeight: '600', color: '#0f172a', whiteSpace: 'pre-wrap' }}>
                    {e.pickup_address || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', verticalAlign: 'top', fontSize: '12px', fontWeight: '700', color: '#1d4ed8' }}>
                    {e.aol || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', verticalAlign: 'top', fontSize: '12px', fontWeight: '700', color: '#1d4ed8' }}>
                    {e.aod || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', verticalAlign: 'top', fontSize: '12px', fontWeight: '600', color: '#0f172a' }}>
                    {e.delivery_at || '-'}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'left', verticalAlign: 'top', fontSize: '12px', fontWeight: '700', color: '#1e3a8a' }}>
                    {e.service || '-'}
                  </td>
                </tr>
                {(e.commodity || e.po_number) && (
                  <tr>
                    <td colSpan={5} style={{ border: '1px solid #94a3b8', background: '#f8fafc', padding: '4px 8px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {e.commodity && (
                          <div>
                            <span style={{ color: '#0f172a', fontWeight: 'bold' }}>Commodity: </span>
                            <strong style={{ color: '#0f172a' }}>{e.commodity}</strong>
                          </div>
                        )}
                        {e.po_number && (
                          <div>
                            <span style={{ color: '#0f172a', fontWeight: 'bold' }}>PO / Ref No: </span>
                            <strong style={{ color: '#0f172a' }}>{e.po_number}</strong>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* ── 5. Package & Cargo Dimensions (Headers 16px, Data 13px, Crisp Borders) ── */}
        {isFCL ? (
          /* SEA FCL: Container Table */
          <div style={{ marginBottom: '14px' }}>
            <div style={{
              background: '#1e293b',
              color: '#fff',
              fontWeight: '700',
              fontSize: '13px',
              letterSpacing: '0.5px',
              padding: '5px 10px',
              textTransform: 'uppercase',
              borderRadius: '4px 4px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>FCL Container Specifications</span>
              {e.sea_gross_weight > 0 && <span style={{ fontSize: '12px' }}>Total Gross Wt: {fmtNum(e.sea_gross_weight)} KGS</span>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ width: '40%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Container Type</th>
                  <th style={{ width: '30%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>General (Dry) Count</th>
                  <th style={{ width: '30%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Reefer Count</th>
                </tr>
              </thead>
              <tbody>
                {(e.containers || []).map((c, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', fontWeight: '600', fontSize: '12px' }}>{c.container_type || '-'}</td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'center', fontSize: '12px' }}>{c.general_count || 0}</td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'center', fontSize: '12px' }}>{c.reefer_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* AIR, SEA LCL, ROAD Cargo Dimensions */
          dimensionRows.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{
                background: '#1e293b',
                color: '#fff',
                fontWeight: '700',
                fontSize: '13px',
                letterSpacing: '0.5px',
                padding: '5px 10px',
                textTransform: 'uppercase',
                borderRadius: '4px 4px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{(isRoadRail || dimensionRows.some(d => d.package_type)) ? 'Cargo Packages & Dimensions' : `Package & Cargo Dimensions (${dimUnit})`}</span>
                {e.special_case_charges && (
                  <span style={{ background: '#d97706', color: '#fff', padding: '2px 8px', borderRadius: '3px', fontSize: '12px' }}>
                    Special Case Charges Applied
                  </span>
                )}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ width: '40px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>#</th>
                    {(isRoadRail || dimensionRows.some(d => d.package_type)) && (
                      <th style={{ width: '120px', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Package Type</th>
                    )}
                    <th style={{ width: '90px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Packages</th>
                    {(isRoadRail || dimensionRows.some(d => d.gross_weight)) && (
                      <th style={{ width: '110px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Gross Wt (kg)</th>
                    )}
                    <th style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Dimensions (L × W × H in {dimUnit})</th>
                    <th style={{ width: '100px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>CBM (m³)</th>
                    {isAirMode && (
                      <th style={{ width: '130px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>
                        Vol. Weight (kg) <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#475569' }}>({e.shipment_scope === 'DOMESTIC' ? '÷5000' : '÷6000'})</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dimensionRows.map((d, idx) => {
                    const l = parseFloat(d.length) || 0;
                    const w = parseFloat(d.width) || 0;
                    const h = parseFloat(d.height) || 0;
                    const pkgs = d.packages || d.no_of_packages || '1';
                    const rowUnit = d.unit || dimUnit;
                    const dimStr = l && w && h ? `${l} × ${w} × ${h} ${rowUnit}` : '-';
                    const hasPkgType = (isRoadRail || dimensionRows.some(x => x.package_type));
                    const hasRowGross = (isRoadRail || dimensionRows.some(x => x.gross_weight));

                    return (
                      <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>{idx + 1}</td>
                        {hasPkgType && (
                          <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
                            {d.package_type || 'Boxes'}
                          </td>
                        )}
                        <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>{pkgs}</td>
                        {hasRowGross && (
                          <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '500' }}>
                            {d.gross_weight ? `${fmtNum(d.gross_weight)} kg` : '-'}
                          </td>
                        )}
                        <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', fontWeight: '500', fontSize: '12px' }}>{dimStr}</td>
                        <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', color: '#047857', fontWeight: '600', fontSize: '12px' }}>
                          {d.cbm ? `${fmtNum(d.cbm, 4)} m³` : '-'}
                        </td>
                        {isAirMode && (
                          <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', color: '#1d4ed8', fontWeight: '600', fontSize: '12px' }}>
                            {d.vol_weight ? `${fmtNum(d.vol_weight)} kg` : '-'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                    <td colSpan={(isRoadRail || dimensionRows.some(d => d.package_type)) ? 2 : 1} style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', textTransform: 'uppercase', fontSize: '12px' }}>
                      Totals / Summary:
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px' }}>
                      {dimensionRows.reduce((sum, d) => sum + (parseInt(d.packages || d.no_of_packages, 10) || 0), 0)} Pkgs
                    </td>
                    {(isRoadRail || dimensionRows.some(d => d.gross_weight)) && (
                      <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px' }}>
                        {(() => {
                          const sumGross = dimensionRows.reduce((sum, d) => sum + (parseFloat(d.gross_weight) || 0), 0);
                          const totalG = sumGross > 0 ? sumGross : (parseFloat(e.gross_weight) || 0);
                          return totalG > 0 ? `${fmtNum(totalG)} kg` : '-';
                        })()}
                      </td>
                    )}
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontSize: '12px', color: '#475569' }}>
                      {!isRoadRail && `Gross Weight: ${e.gross_weight ? `${fmtNum(e.gross_weight)} KGS` : '-'}`}
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', color: '#047857', fontSize: '12px' }}>
                      {e.total_cbm ? `${fmtNum(e.total_cbm, 4)} m³` : '-'}
                    </td>
                    {isAirMode && (
                      <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', color: '#1d4ed8', fontSize: '12px' }}>
                        {e.total_volume_weight ? `${fmtNum(e.total_volume_weight)} kg` : '-'}
                      </td>
                    )}
                  </tr>
                  {isAirMode && (
                    <tr style={{ background: '#faf5ff', fontWeight: 'bold', borderTop: '2px solid #94a3b8' }}>
                      <td colSpan={4} style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontSize: '12px', color: '#475569' }}>
                        Gross Weight: <strong style={{ color: '#0f172a' }}>{e.gross_weight ? `${fmtNum(e.gross_weight)} KGS` : '-'}</strong>
                      </td>
                      <td colSpan={2} style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'center', fontSize: '12px', color: '#6b21a8' }}>
                        Chargeable Weight: <strong style={{ fontSize: '12px', display: 'block' }}>{e.chargeable_weight ? `${fmtNum(e.chargeable_weight)} KGS` : '-'}</strong>
                        {e.special_case_charges && e.airline_actual_weight && (
                          <span style={{ fontSize: '12px', color: '#b45309', display: 'block' }}>
                            (Airline Weight: {fmtNum(e.airline_actual_weight)} kg)
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          )
        )}

        {/* ── 6. Charge Group Tables (Headers & Subtotal 16px, Items 13px, Crisp Borders) ── */}
        {(e.charge_groups || []).map((group, gi) => {
          const items = group.items || [];
          const hasAirline = items.some(i => i.airline_vendor && i.airline_vendor.trim());
          const hasTariff = items.some(i => i.tariff && i.tariff.trim());

          return (
            <div key={gi} className="avoid-break" style={{ marginBottom: '16px' }}>
              <div style={{
                background: '#0f172a',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: '4px 4px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '13px', fontWeight: '700' }}>
                  GROUP {group.group_label}: {group.group_name?.toUpperCase()}
                </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#fef08a' }}>
                  Subtotal: {fmtAmt(group.group_total, currSymbol)}
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ width: '45px', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Sr</th>
                    <th style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Description of Charges</th>
                    {hasAirline && <th style={{ width: '16%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Carrier / Vendor</th>}
                    {hasTariff && <th style={{ width: '13%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Tariff</th>}
                    <th style={{ width: '14%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Rate / Amount</th>
                    <th style={{ width: '9%', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Qty</th>
                    <th style={{ width: '8%', border: '1px solid #94a3b8', padding: '6px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Unit</th>
                    <th style={{ width: '18%', border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>Total Amount ({e.currency || 'INR'})</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6 + (hasAirline ? 1 : 0) + (hasTariff ? 1 : 0)} style={{ border: '1px solid #94a3b8', textAlign: 'center', color: '#94a3b8', padding: '10px', fontSize: '12px' }}>
                        No charge items in this group
                      </td>
                    </tr>
                  ) : (
                    items.map((item, ii) => (
                      <tr key={ii} style={{ background: ii % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>{ii + 1}</td>
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 10px', fontWeight: '500', fontSize: '12px' }}>{item.description || '-'}</td>
                        {hasAirline && <td style={{ border: '1px solid #94a3b8', padding: '5px 10px', fontSize: '12px' }}>{item.airline_vendor || '-'}</td>}
                        {hasTariff && <td style={{ border: '1px solid #94a3b8', padding: '5px 10px', fontSize: '12px' }}>{item.tariff || '-'}</td>}
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 10px', textAlign: 'right', fontSize: '12px' }}>{fmtAmt(item.amount, currSymbol)}</td>
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'center', fontSize: '12px' }}>{fmtNum(item.qty, 2)}</td>
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>{item.qty_unit || '-'}</td>
                        <td style={{ border: '1px solid #94a3b8', padding: '5px 10px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>{fmtAmt(item.total, currSymbol)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', fontWeight: 'bold' }}>
                    <td
                      colSpan={5 + (hasAirline ? 1 : 0) + (hasTariff ? 1 : 0)}
                      style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'right', textTransform: 'uppercase', fontSize: '13px', color: '#1e293b' }}
                    >
                      Total {group.group_name} ({group.group_label}):
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '5px 8px', textAlign: 'right', fontSize: '13px', color: '#1e3a8a', fontWeight: '800' }}>
                      {fmtAmt(group.group_total, currSymbol)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* ── 7. Consolidated Summary of Quotation (Shifted to Right Side) ── */}
        <div className="avoid-break" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div style={{ width: '56%', minWidth: '380px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  <th style={{ color: '#fff', border: '1px solid #0f172a', textAlign: 'left', width: '60%', fontSize: '13px', padding: '5px 8px', fontWeight: '700' }}>
                    Consolidated Summary of Quotation
                  </th>
                  <th style={{ color: '#fff', border: '1px solid #0f172a', textAlign: 'right', width: '40%', fontSize: '13px', padding: '5px 8px', fontWeight: '700' }}>
                    Total Amount ({e.currency || 'INR'})
                  </th>
                </tr>
              </thead>
              <tbody>
                {(e.charge_groups || []).map((g, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', fontWeight: '500', fontSize: '12px' }}>
                      Group {g.group_label}: {g.group_name}
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>
                      {fmtAmt(g.group_total, currSymbol)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#eff6ff', borderTop: '2px solid #1e3a8a' }}>
                  <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', fontWeight: '800', fontSize: '13px', textTransform: 'uppercase', color: '#1e3a8a' }}>
                    Grand Total ({e.currency || 'INR'})
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '6px 8px', fontWeight: '900', fontSize: '13px', textAlign: 'right', color: '#1e3a8a' }}>
                    {fmtAmt(grandTotal, currSymbol)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {grandTotal > 0 && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#334155', fontStyle: 'italic', textAlign: 'right', width: '100%' }}>
              <strong>Amount in Words: </strong> {numberToWordsINR(grandTotal, e.currency || 'INR')}
            </div>
          )}
        </div>

        {/* ── 8. Highlight Notes (11px Uniform) ────────────────────────── */}
        {e.highlight_notes && (
          <div className="avoid-break" style={{
            border: '1px solid #fed7aa',
            borderRadius: '6px',
            background: '#fffaf5',
            padding: '10px 14px',
            marginBottom: '14px',
            fontSize: '12px'
          }}>
            <div style={{ fontWeight: '700', color: '#c2410c', textTransform: 'uppercase', marginBottom: '5px', fontSize: '12px' }}>
              Important Quotation Notes &amp; Assumptions
            </div>
            <div style={{ whiteSpace: 'pre-line', color: '#9a3412', lineHeight: 1.45, fontWeight: '500' }}>
              {e.highlight_notes}
            </div>
          </div>
        )}

        {/* ── 9. Remarks Numbered List (13px Uniform) ──────────────────── */}
        {remarksList.length > 0 && (
          <div className="avoid-break" style={{ marginBottom: '16px', fontSize: '12px' }}>
            <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.4px' }}>
              Terms &amp; Conditions / Standard Remarks:
            </div>
            <ol style={{ paddingLeft: '20px', margin: 0, lineHeight: '1.6', color: '#334155', fontSize: '12px' }}>
              {remarksList.map((r, i) => (
                <li key={i} style={{ marginBottom: '3px' }}>{r}</li>
              ))}
            </ol>
          </div>
        )}

        {/* ── 10. Signature Blocks (Customer Acceptance + Company Signatory - 13px) ── */}
        <div className="avoid-break" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          marginTop: '22px',
          paddingTop: '14px',
          borderTop: '1px dashed #cbd5e1'
        }}>
          {/* Customer Acceptance */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', background: '#fafafa' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Customer Confirmation &amp; Acceptance
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '30px' }}>
              Authorized Signature &amp; Company Stamp
            </div>
            <div style={{ marginTop: '8px', borderTop: '1px solid #cbd5e1', paddingTop: '3px', fontSize: '12px', color: '#64748b' }}>
              Date: ________________________
            </div>
          </div>

          {/* Company Signatory */}
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              {(e.signature_text || `Thanking you in advance.\n\nFor, ${cmpName}`)
                .split('\n')
                .map((line, lIdx) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <div key={lIdx} style={{ height: '8px' }} />;
                  const isForCompany = trimmed.toLowerCase().startsWith('for,') || trimmed.toLowerCase().startsWith('for ');
                  const isAuth = trimmed.toLowerCase().includes('authorized signatory');
                  return (
                    <div
                      key={lIdx}
                      style={{
                        fontSize: '12px',
                        fontWeight: isForCompany || isAuth ? '700' : '400',
                        color: isForCompany ? '#0f172a' : (isAuth ? '#0f172a' : '#475569'),
                        textTransform: isForCompany ? 'uppercase' : 'none',
                        marginTop: isForCompany ? '2px' : '0'
                      }}
                    >
                      {line}
                    </div>
                  );
                })}
            </div>
            <div style={{ marginTop: '30px' }}>
              {!((e.signature_text || '').toLowerCase().includes('authorized signatory')) && (
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>(Authorized Signatory)</div>
              )}
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>This is a computer generated quote</div>
            </div>
          </div>
        </div>

        {/* ── 11. Official Footer ─────────────────────────────────────── */}
        <div style={{
          marginTop: '20px',
          paddingTop: '8px',
          borderTop: '1px solid #e2e8f0',
          textAlign: 'center',
          fontSize: '12px',
          color: '#64748b',
          lineHeight: '1.45'
        }}>
          <div style={{ fontWeight: '700', color: '#334155', fontSize: '12.5px' }}>{cmpName}</div>
          <div>{cmpAddr}</div>
          <div><strong style={{ color: '#0f172a' }}>GSTIN:</strong> {cmpGst} {cmpPan ? <>• <strong style={{ color: '#0f172a' }}>PAN:</strong> {cmpPan}</> : ''} • <strong style={{ color: '#0f172a' }}>Email:</strong> {cmpEmail} • <strong style={{ color: '#0f172a' }}>Web:</strong> {cmpWeb}</div>
        </div>

      </div>
    </>
  );
}
